import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Upload, Trash2, Star, Image as ImageIcon, MapPin, CheckCircle2, AlertCircle, Search, Loader2, Video, Bot } from "lucide-react";

const SLOTS = [
  { key: "exterior", label: "Exterior", required: true },
  { key: "interior", label: "Interior", required: true },
  { key: "experience", label: "Experience / Vibe", required: true },
  { key: "food", label: "Food & Drink" },
  { key: "menu", label: "Menu" },
  { key: "gallery", label: "Gallery / Art" },
  { key: "event", label: "Event" },
  { key: "team", label: "Team / Portrait" },
  { key: "video", label: "Video" },
];
const BE = process.env.REACT_APP_BACKEND_URL;
const full = (u) => (u?.startsWith("/api") ? BE + u : u);

function MediaTile({ m, onChange, onDelete }) {
  return (
    <div className="relative group border border-white/10 bg-black" data-testid={`media-tile-${m.id}`}>
      {m.media_type === "video" ? (
        <video src={full(m.url)} className="w-full h-28 object-cover" muted />
      ) : (
        <img src={full(m.url)} alt={m.caption} className="w-full h-28 object-cover" />
      )}
      <div className="absolute top-1 left-1 flex gap-1">
        {m.geo && <span className="bg-black/70 text-[#00FF66] text-[9px] px-1 py-0.5 flex items-center gap-0.5" title={`${m.geo.lat}, ${m.geo.lng} (${m.geo_source})`}><MapPin size={9} /> geo</span>}
        <span className={`text-[9px] px-1 py-0.5 ${m.source === "bot" ? "bg-[#00E5FF]/80 text-black" : "bg-white/20 text-white"}`}>{m.source}</span>
      </div>
      {m.needs_review && <span className="absolute top-1 right-1 bg-[#FFEA00] text-black text-[9px] px-1 py-0.5 font-bold">REVIEW</span>}
      <div className="absolute inset-x-0 bottom-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-around py-1.5">
        <button onClick={() => onChange(m, { guide_featured: !m.guide_featured })} title="Use as guide thumbnail" data-testid={`media-guide-${m.id}`} className={m.guide_featured ? "text-[#FFEA00]" : "text-neutral-400 hover:text-white"}><Star size={15} fill={m.guide_featured ? "#FFEA00" : "none"} /></button>
        <button onClick={() => onChange(m, { in_gallery: !m.in_gallery })} title="Show in profile gallery" data-testid={`media-gallery-${m.id}`} className={m.in_gallery ? "text-[#00E5FF]" : "text-neutral-400 hover:text-white"}><ImageIcon size={15} /></button>
        {m.needs_review && <button onClick={() => onChange(m, { needs_review: false, approved: true, in_gallery: true })} title="Approve & publish" data-testid={`media-approve-${m.id}`} className="text-[#00FF66] hover:text-white"><CheckCircle2 size={15} /></button>}
        <button onClick={() => onDelete(m)} title="Delete" data-testid={`media-delete-${m.id}`} className="text-[#FF0055] hover:text-white"><Trash2 size={15} /></button>
      </div>
    </div>
  );
}

function SlotRow({ slot, items, venueSlug, reload }) {
  const fileRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("venue_slug", venueSlug);
    fd.append("slot", slot.key);
    try { await api.post("/admin/media/upload", fd); toast.success(`Uploaded to ${slot.label}`); reload(); }
    catch { toast.error("Upload failed"); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const change = async (m, patch) => { await api.put(`/admin/media/${m.id}`, patch); reload(); };
  const del = async (m) => { await api.delete(`/admin/media/${m.id}`); toast.success("Deleted"); reload(); };
  const missing = slot.required && items.length === 0;
  return (
    <div className="border border-white/10 bg-[#141414] p-4" data-testid={`slot-${slot.key}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-white font-medium text-sm">{slot.label}</span>
          {slot.required && (missing
            ? <span className="text-[#FF0055] text-[10px] flex items-center gap-1"><AlertCircle size={11} /> REQUIRED — missing</span>
            : <span className="text-[#00FF66] text-[10px] flex items-center gap-1"><CheckCircle2 size={11} /> covered</span>)}
          <span className="text-neutral-600 text-xs">({items.length})</span>
        </div>
        <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid={`upload-${slot.key}`} className="flex items-center gap-1.5 text-xs border border-white/15 text-neutral-200 hover:text-white hover:border-[#00E5FF] px-3 py-1.5 disabled:opacity-50">
          {busy ? <Loader2 size={13} className="animate-spin" /> : (slot.key === "video" ? <Video size={13} /> : <Upload size={13} />)} Upload
        </button>
        <input ref={fileRef} type="file" accept={slot.key === "video" ? "video/*" : "image/*"} onChange={upload} className="hidden" data-testid={`file-${slot.key}`} />
      </div>
      {items.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2">
          {items.map((m) => <MediaTile key={m.id} m={m} onChange={change} onDelete={del} />)}
        </div>
      )}
    </div>
  );
}

export default function VenueMedia() {
  const [coverage, setCoverage] = useState({ summary: {}, venues: [] });
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [media, setMedia] = useState([]);
  const [loadingCov, setLoadingCov] = useState(true);
  const [harvest, setHarvest] = useState(null);

  const loadCoverage = () => {
    setLoadingCov(true);
    api.get("/admin/media/coverage").then((r) => setCoverage(r.data)).finally(() => setLoadingCov(false));
  };
  useEffect(() => { loadCoverage(); }, []);
  useEffect(() => {
    const poll = () => api.get("/admin/media/harvest/status").then((r) => setHarvest(r.data)).catch(() => {});
    poll();
    const t = setInterval(poll, 4000);
    return () => clearInterval(t);
  }, []);

  const openVenue = (v) => {
    setSelected(v);
    api.get("/admin/media", { params: { venue_slug: v.slug } }).then((r) => setMedia(r.data));
  };
  const reloadMedia = () => {
    if (selected) api.get("/admin/media", { params: { venue_slug: selected.slug } }).then((r) => setMedia(r.data));
    loadCoverage();
  };
  const harvestAll = async () => {
    setHarvest({ state: "running", photos: 0, venues: 0 });
    try { const { data } = await api.post("/admin/media/harvest"); toast.success(data.message); }
    catch { toast.error("Failed to start harvest"); }
  };
  const harvestVenue = async (slug) => {
    setHarvest({ state: "running", photos: 0, venues: 0 });
    try { const { data } = await api.post(`/admin/media/harvest?venue_slug=${slug}`); toast.success(data.message); setTimeout(reloadMedia, 6000); }
    catch { toast.error("Failed"); }
  };
  const harvesting = harvest?.state === "running";

  const s = coverage.summary || {};
  const filtered = (coverage.venues || []).filter((v) => v.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="venue-media-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
        <div>
          <h2 className="font-display font-bold text-2xl text-white">Venue Media Vault</h2>
          <p className="text-neutral-500 text-sm mt-1 max-w-2xl">Every venue expects <b className="text-white">Exterior, Interior & Experience</b> shots. Upload team photos (phone GPS is read automatically; missing GPS is stamped with the venue's location), tag which appear in the gallery, and star the one used as the guide thumbnail.</p>
        </div>
        <div className="flex items-end gap-3">
          <button onClick={harvestAll} disabled={harvesting} data-testid="harvest-all-btn" className="flex items-center gap-2 bg-[#00E5FF] text-black text-sm font-bold px-4 py-2.5 hover:bg-[#5eefff] disabled:opacity-50">
            {harvesting ? <Loader2 size={15} className="animate-spin" /> : <Bot size={15} />}
            {harvesting ? "Harvesting…" : "Bot: Fill Missing Photos"}
          </button>
          {[["Venues", s.total], ["Fully covered", s.covered], ["Need photos", s.uncovered]].map(([l, v]) => (
            <div key={l} className="border border-white/10 bg-[#141414] px-4 py-2 text-center">
              <div className="font-display font-extrabold text-xl text-white">{v ?? "—"}</div>
              <div className="text-neutral-500 text-[10px]">{l}</div>
            </div>
          ))}
        </div>
      </div>
      {harvest?.state === "done" && harvest?.photos > 0 && (
        <div className="border border-[#00E5FF]/30 bg-[#00E5FF]/5 text-[#00E5FF] text-xs px-4 py-2 mb-4" data-testid="harvest-result">
          VisitRiNoBot pulled {harvest.photos} photo(s) across {harvest.venues} venue(s) — find them tagged "REVIEW" and approve the good ones.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* venue list */}
        <div className="lg:col-span-1 border border-white/10 bg-[#0f0f0f]">
          <div className="p-3 border-b border-white/10 relative">
            <Search size={14} className="absolute left-5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search venues..." data-testid="venue-media-search" className="w-full bg-[#141414] border border-white/10 pl-8 pr-3 py-2 text-sm text-white focus:outline-none focus:border-[#00E5FF]" />
          </div>
          <div className="max-h-[70vh] overflow-y-auto">
            {loadingCov ? <div className="text-neutral-500 text-sm p-4">Loading…</div> : filtered.slice(0, 300).map((v) => (
              <button key={v.slug} onClick={() => openVenue(v)} data-testid={`venue-row-${v.slug}`}
                className={`w-full text-left px-4 py-2.5 border-b border-white/5 flex items-center justify-between gap-2 hover:bg-white/5 ${selected?.slug === v.slug ? "bg-white/10" : ""}`}>
                <span className="text-sm text-neutral-200 truncate">{v.name}</span>
                {v.missing.length === 0
                  ? <CheckCircle2 size={14} className="text-[#00FF66] flex-shrink-0" />
                  : <span className="text-[10px] text-[#FF0055] flex-shrink-0">{v.missing.length} missing</span>}
              </button>
            ))}
          </div>
        </div>

        {/* editor */}
        <div className="lg:col-span-2">
          {!selected ? (
            <div className="border border-dashed border-white/10 h-full min-h-[300px] flex items-center justify-center text-neutral-500 text-sm">Select a venue to manage its photos & video</div>
          ) : (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-display font-bold text-xl text-white">{selected.name}</h3>
                <button onClick={() => harvestVenue(selected.slug)} data-testid="harvest-venue-btn" className="flex items-center gap-2 text-xs border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 px-3 py-1.5">
                  <Bot size={13} /> Fill photos with bot
                </button>
              </div>
              <div className="space-y-3">
                {SLOTS.map((slot) => (
                  <SlotRow key={slot.key} slot={slot} venueSlug={selected.slug}
                    items={media.filter((m) => m.slot === slot.key)} reload={reloadMedia} />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
